import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Play } from "lucide-react";
import { PSS, PSS_OPTIONS, STRESS_BANDS, pssScore, stressBand } from "@/components/tools/pss";
import { ToolCrisis, ToolInvite } from "@/components/tools/ToolFinish";
import { resetTrack, track } from "@/lib/toolTrack";

const Result = ({ band, tool, onAgain }) => {
  const b = STRESS_BANDS[band];
  return (
    <div className="mm-fade-up" data-testid="tool-stress-result">
      <div className="rounded-3xl p-6 text-center" style={{ background: b.bg, color: b.ink }}>
        <div className="text-5xl" aria-hidden>{b.emoji}</div>
        <h3 className="text-2xl font-bold mt-2" data-testid="tool-stress-band">{b.label}</h3>
        <p className="text-sm opacity-80 mt-1">Based on how the last month has felt. This is not a diagnosis.</p>
      </div>
      <ul className="mt-4 space-y-2 text-sm">{b.tips.map((t) => <li key={t} className="flex gap-2"><span aria-hidden>🌱</span>{t}</li>)}</ul>
      <div className="flex flex-wrap gap-3 mt-3 text-sm font-semibold"><Link to="/tools/physiological-sigh" className="text-mm-primary">Try a physiological sigh →</Link><Link to="/tools/worry-dump" className="text-mm-primary">Try a worry dump →</Link></div>
      {band === "high" && <ToolCrisis />}
      <ToolInvite tool={tool} onAgain={onAgain} card={{ line: `My stress check: ${b.label.replace(" stress", "")}`, sub: "How stressed are you? Check free.", emoji: b.emoji, bg: b.bg, ink: b.ink }} />
    </div>
  );
};

export const StressTool = ({ tool }) => {
  const [answers, setAnswers] = useState(null);
  const done = answers && answers.length === PSS.length;
  const begin = () => { resetTrack(tool.slug); setAnswers([]); track(tool.slug, "start"); };
  const pick = (v) => { const next = [...answers, v]; setAnswers(next); if (next.length === PSS.length) track(tool.slug, "finish"); };
  if (!answers) return (
    <div className="text-center"><p className="text-mm-muted mb-5">10 quick questions about the last month. Your answers stay on this device.</p>
      <button onClick={begin} className="mm-btn !px-8 !py-3.5 text-lg" data-testid="tool-start"><Play size={18} />Start</button></div>
  );
  if (done) return <Result band={stressBand(pssScore(answers))} tool={tool} onAgain={begin} />;
  const i = answers.length;
  return (
    <div data-testid="tool-stress-question">
      <div className="flex items-center justify-between text-sm text-mm-muted">
        <button onClick={() => setAnswers(answers.slice(0, -1))} disabled={!i} className="inline-flex items-center gap-1 disabled:opacity-30" data-testid="tool-stress-back"><ChevronLeft size={16} />Back</button>
        <span data-testid="tool-stress-progress">{i + 1} of {PSS.length}</span>
      </div>
      <div className="h-1.5 rounded-full bg-mm-primary/15 mt-2"><div className="h-full rounded-full bg-mm-primary transition-[width] duration-300" style={{ width: `${(i / PSS.length) * 100}%` }} /></div>
      <p className="text-lg font-semibold mt-5">In the last month, how often have you {PSS[i]}</p>
      <div className="grid gap-2 mt-4">
        {PSS_OPTIONS.map((o, v) => <button key={o} onClick={() => pick(v)} className="mm-soft rounded-2xl px-4 py-3 text-left font-medium hover:bg-mm-primary/10 transition-colors" data-testid={`tool-stress-opt-${v}`}>{o}</button>)}
      </div>
    </div>
  );
};
